import { registerServiceWorker } from './service-worker-manager';
/**
 * Crash Monitor Client
 *
 * Detects page crashes/unresponsiveness by sending heartbeat messages to a
 * service worker. If the main thread crashes, heartbeats stop and the SW
 * logs the crash event.
 */

// ============================================================================
// CRASH MONITORING SPECIFIC LOGIC
// These functions implement the crash detection feature
// ============================================================================

/**
 * Initialize crash monitoring
 *
 * @param {Object} config - Configuration object
 * @returns {Object} - Monitor object with cleanup method
 */
function initCrashMonitoring(config = {}) {
  let heartbeatIntervalId = null;
  let isPaused = false;
  const HEARTBEAT_INTERVAL = 2000; // 2 seconds

  /**
   * Send a message to the service worker
   */
  function sendMessage(type) {
    if (!navigator.serviceWorker.controller) {
      return;
    }
    navigator.serviceWorker.controller.postMessage({
      ...config,
      type,
      pageId: config.pageSessionId,
      slug: typeof window !== 'undefined' ? window.location.pathname : '',
      eventUri: typeof window !== 'undefined' ? window.location.href : '',
      contextPageType: 'buzz',
      contextPageId: config.buzzId
    });
  }

  /**
   * Send a heartbeat message to the service worker
   */
  function sendHeartbeat() {
    if (isPaused) {
      return;
    }
    sendMessage('heartbeat');
  }

  /**
   * Start sending heartbeats
   */
  function startHeartbeat() {
    if (heartbeatIntervalId) {
      clearInterval(heartbeatIntervalId);
    }

    // Send first heartbeat immediately
    sendHeartbeat();

    // Then send every HEARTBEAT_INTERVAL
    heartbeatIntervalId = setInterval(() => {
      sendHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }

  /**
   * Stop sending heartbeats
   */
  function stopHeartbeat() {
    if (heartbeatIntervalId) {
      clearInterval(heartbeatIntervalId);
      heartbeatIntervalId = null;
    }
  }

  /**
   * Set up visibility change handler
   */
  function setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        isPaused = true;
        stopHeartbeat();
        sendMessage('clear');
      } else {
        isPaused = false;
        // Small delay before resuming to ensure clear message was processed
        setTimeout(() => {
          startHeartbeat();
        }, 100);
      }
    });
  }

  /**
   * Set up pagehide handler
   * More reliable than beforeunload for detecting navigation, especially with bfcache
   */
  function setupPageHideHandler() {
    window.addEventListener('pagehide', () => {
      stopHeartbeat();
      sendMessage('clear');
    });
  }

  /**
   * Clean up crash monitoring
   */
  function cleanup() {
    stopHeartbeat();
    sendMessage('clear');
  }

  // Initialize
  setupVisibilityHandler();
  setupPageHideHandler();

  // Only start heartbeat if page is visible
  // Background tabs (opened via ctrl+click, middle-click) should not be monitored
  // until the user actually views them - prevents false positives from tab closures
  // If hidden, the visibilitychange handler will start heartbeat when page becomes visible
  if (document.visibilityState === 'visible') {
    startHeartbeat();
  }

  // Return monitor object
  return {
    cleanup
  };
}

/**
 * Initialize crash monitoring
 *
 * @param {Object} config - Configuration object
 * @param {string|number} config.buzzId - The buzz ID for this page (required for crash correlation)
 * @param {string|number} config.service - The service rendering this page (required for crash correlation)
 * @param {string|number} config.destination - The destination for this page (required for crash correlation)
 * @param {boolean} config.isQuiz - Whether this page is a quiz
 * @param {string|number} config.randomUserUuid - The random user UUID
 * @param {string|number} config.clientSessionId - The client session ID
 * @param {string|number} config.clientUuid - The client UUID
 * @param {string|number} config.pageSessionId - The page session ID
 * @param {string|number} config.previousPageSessionId - The previous page session ID
 * @param {Function} getFeatureFlagValue - ABeagle context with getFeatureFlagValue
 * @returns {Promise<Object|null>} - Monitor object with cleanup method, or null if disabled
 */

export default async function initCrashMonitor(config = {}, getFeatureFlagValue) {
  if (!getFeatureFlagValue) {
    throw new Error('Missing getFeatureFlagValue function');
  }

  // Check if crash monitoring is enabled
  const isEnabled = getFeatureFlagValue('bf_crash_monitor_enabled');
  if (!isEnabled) {
    console.log('[Crash Monitor] Disabled by feature flag');
    navigator.serviceWorker.controller?.postMessage({ type: 'disable_crash_monitor' });
    return null;
  }

  // Limit the crash monitoring to quizzes as that's what we're trying to diagnose
  if (!config.isQuiz) {
    return null;
  }

  // Skip Pinterest in-app browser traffic - their webview doesn't fire pagehide
  // when user closes it, causing false positives on every session exit
  if (typeof window !== 'undefined' && window.location.search.includes('epik=')) {
    return null;
  }

  try {
    // Initialize service worker (generic, reusable)
    const registration = await registerServiceWorker();

    // If no controller, bail out gracefully
    if (!registration) {
      return null;
    }
    navigator.serviceWorker.controller?.postMessage({ type: 'enable_crash_monitor' });
    // Initialize crash monitoring (specific to crash detection)
    return initCrashMonitoring(config);
  } catch (err) {
    return null;
  }
}
